import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, ArrowRight, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import CountdownTimer from '../components/CountdownTimer';
import instructorImg from '../images/Alex.jpg';
import aiDevImg from '../images/Highlight1.jpg';
import sessionsImg from '../images/Highlight2.jpg';
import mentorsImg from '../images/Highlight3.jpg';
import communityImg from '../images/Highlight4.jpg';
import roboticsImg from '../images/Highlight6.jpeg';
import dataVizImg from '../images/ai_dev.jpg';
import heroImg from '../images/hero1.jpg';

const AnimatedCounter = ({ end, duration = 2000, suffix = "", decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (countRef.current) {
      observer.observe(countRef.current);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      // ease out cubic
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      setCount(easeProgress * end);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={countRef}>{count.toFixed(decimals)}{suffix}</span>;
};

// Custom Brand Icons (Removed in Lucide v1)
const Linkedin = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const Instagram = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);


const Home = () => {
  const navigate = useNavigate();
  const webinarDate = "2026-05-06T10:00:00+05:30";

  const highlights = [
    {
      title: "AI Development",
      image: aiDevImg
    },
    {
      title: "Interactive Sessions",
      image: sessionsImg
    },
    {
      title: "Expert Mentors",
      image: mentorsImg
    },
    {
      title: "Global Community",
      image: communityImg
    },
    {
      title: "Future of Robotics",
      image: roboticsImg
    },
    {
      title: "Data Visualization",
      image: dataVizImg
    }
  ];

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-banner">
          <img src={heroImg} alt="Webinar Banner" />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="hero-content hero-content-alt"
          >
            <span className="badge">OFFLINE WORKSHOP</span>
            <h1 className="hero-title hero-title-white">Building <span className="text-gradient">Autonomous AI Agents</span> Using LLMs</h1>

            <div className="countdown-wrapper-hero">
              <CountdownTimer targetDate={webinarDate} />
            </div>

            <p className="hero-desc hero-desc-white">
              Join us for a hands-on offline workshop on Building Autonomous AI Agents using LLMs.

            </p>

            <div className="hero-actions">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                <button onClick={() => navigate('/register')} className="btn-primary">
                  Register Now <ArrowRight size={18} />
                </button>
                <div className="price-tag">
                  Only <span className="price">₹149</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Details Section */}
      <section className="details-section">

        <div className="container">
          <div className="details-cards-vertical">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="info-card info-card--date">
              <div className="info-card-icon">
                <Calendar size={26} />
              </div>
              <div className="info-card-body">
                <span className="info-card-label">Event Date</span>
                <h3 className="info-card-value">May 6th, 2026</h3>
                <p className="info-card-sub">Wednesday</p>
              </div>
              <div className="info-card-accent"></div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.15 }} className="info-card info-card--time">
              <div className="info-card-icon">
                <Clock size={26} />
              </div>
              <div className="info-card-body">
                <span className="info-card-label">Session Timings</span>
                <h3 className="info-card-value">10 AM – 6 PM</h3>
                <p className="info-card-sub">Session 1: 10 AM – 1 PM</p>
                <p className="info-card-sub">Session 2: 3 PM – 6 PM</p>
              </div>
              <div className="info-card-accent"></div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }} className="info-card info-card--venue">
              <div className="info-card-icon">
                <MapPin size={26} />
              </div>
              <div className="info-card-body">
                <span className="info-card-label">Event Venue</span>
                <h3 className="info-card-value">Nagercoil, TN</h3>
                <p className="info-card-sub">57/57b, Rajakkamangalam Rd</p>
              </div>
              <div className="info-card-accent"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Instructor Section */}
      <section className="instructor-section">
        <div className="container">
          <div className="instructor-card">
            <div className="instructor-image">
              <img src={instructorImg} alt="Instructor" />
            </div>
            <div className="instructor-info">
              <span className="label">MEET YOUR INSTRUCTOR</span>
              <h2>MITHUN K</h2>
              <p className="instructor-desc">
                Junior Software Developer | Technical Trainer | QA & Testing
                Python • React • Backend Development
              </p>
              <div className="instructor-stats">
                <div className="stat">
                  <span className="stat-value"><AnimatedCounter end={3} suffix="k+" decimals={0} /></span>
                  <span className="stat-label">Students</span>
                </div>
                <div className="stat">
                  <span className="stat-value"><AnimatedCounter end={4.9} suffix="/5" decimals={1} /></span>
                  <span className="stat-label">Rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topics Section */}
      <section className="highlights-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Webinar Highlights</h2>
            <p className="section-subtitle">What you can expect from this interactive event.</p>
          </div>

          <div className="gallery-grid">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="gallery-item"
              >
                <div className="gallery-image-container">
                  <img src={item.image} alt={item.title} />
                  <div className="gallery-overlay">
                    <span className="gallery-title">{item.title}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer / Links Section */}
      <footer className="modern-footer">
        <div className="footer-glow"></div>
        <div className="container">
          <div className="footer-content-grid">
            <div className="footer-column brand-col">
              <h3 className="footer-logo">NIM <span>TECHNOLOGIES</span></h3>
              <p className="footer-tagline">Empowering the next generation of AI developers through immersive, expert-led workshops and hands-on training.</p>
              <div className="social-group">
                <a href="https://www.linkedin.com/company/nim-academy/posts/?feedView=all" target="_blank" rel="noreferrer" className="social-circle" aria-label="LinkedIn">
                  <Linkedin size={20} />
                </a>
                <a href="https://www.instagram.com/nim_academy?igsh=MXI5azN0NGtyYTgxMQ==" target="_blank" rel="noreferrer" className="social-circle" aria-label="Instagram">
                  <Instagram size={20} />
                </a>
              </div>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Quick Links</h4>
              <ul className="footer-links-list">
                <li><a href="https://nimtechnologies.in/" target="_blank" rel="noreferrer"><ExternalLink size={14} /> Official Website</a></li>
                <li><a href="https://www.nimacademy.in/" target="_blank" rel="noreferrer"><ExternalLink size={14} /> NIM Academy</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Webinar Details</h4>
              <ul className="footer-links-list">
                <li><span className="footer-info-text">May 6, 2026</span></li>
                <li><span className="footer-info-text">10:00 AM - 6:00 PM</span></li>
                <li><span className="footer-info-text">Nagercoil, Tamil Nadu</span></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Contact Us</h4>
              <p className="footer-contact-text">Questions about the event?</p>
              <a href="mailto:inimtechnologiez@gmail.com
" className="footer-email-link">nimtechnologiez@gmail.com

              </a>
            </div>
          </div>

          <div className="footer-bottom-divider"></div>

          <div className="footer-bottom-info">
            <p>&copy; 2026 NIM Technologies. All rights reserved.</p>
            <div className="footer-legal-links">
              <a href="#privacy">Privacy</a>
              <a href="#terms">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
